import {
  Box,
  Button,
  Card,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useRetractOrderSAV from '../../../../../../hooks/orders/useRetractOrderSAV';
import UserAssignmentAutocomplete from '../../../../../utils/common/UserAssignmentAutocomplete';
import RetractionConfirmModal from './RetractionConfirmModal.jsx';
import RetractionFeesForm from './RetractionFeesForm.jsx';
import { setSnackBar } from '../../../../../../states/app/staticSlice.js';

const RetractionFormSAV = ({
  orderRef,
  handleShowComponent,
  orderDetails,
  userId,
}) => {
  const dispatch = useDispatch();
  const { retractOrderSAV } = useRetractOrderSAV();
  const orderRetractionReasons = useSelector(
    (state) => state?.static?.orderRetractionReasons
  );

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [formData, setFormData] = useState({
    ticketReference: orderDetails?.ticket?.reference,
    orderReference: orderDetails?.reference,
    comment: '',
    reason: '',
    retractionFees: '',
    assignedTo: null,
    isDraft: true,
    currencyCode: '',
  });
  const [formError, setFormError] = useState({
    comment: null,
    reason: null,
    retractionFees: null,
    currencyCode: null,
  });

  const getRetractionFees = (retractionFees) => {
    setFormData((prevData) => {
      if (
        JSON.stringify(prevData.retractionFees) !==
        JSON.stringify(retractionFees)
      ) {
        const numericFees = {
          ...retractionFees,
          refundAmount: parseFloat(retractionFees.refundAmount) || 0,
          retractFeeAmount: parseFloat(retractionFees.retractFeeAmount) || 0,
          partialRefundAmount:
            parseFloat(retractionFees.partialRefundAmount) || 0,
          productsAmount: parseFloat(retractionFees.productsAmount) || 0,
        };

        return {
          ...prevData,
          ...numericFees,
        };
      }
      return prevData;
    });
  };

  const handleFormChange = ({ key, value }) => {
    setFormData((prevData) => ({
      ...prevData,
      [key]: value,
    }));
  };

  const validateFields = () => {
    let isValid = true;
    const newErrors = {};

    if (!formData?.reason) {
      newErrors.reason = 'Veuillez sélectionner un motif de rétractation';
      isValid = false;
    }

    if (!formData?.comment || formData.comment?.length < 10) {
      newErrors.comment = 'Le commentaire doit faire au moins 10 caractères';
      isValid = false;
    }

    if (!formData?.currencyCode) {
      newErrors.currencyCode = "La devise n'est pas définie";
      isValid = false;
    }

    setFormError(newErrors);
    return isValid;
  };

  const handleOpenConfirm = () => {
    if (validateFields()) {
      setIsConfirmOpen(true);
    } else {
      console.error('formError', formError);
    }
  };

  const onCloseModalConfirm = () => {
    setIsConfirmOpen(false);
    setConfirmLoading(false);
  };

  const handleConfirm = async () => {
    setConfirmLoading(true);
    try {
      console.log('handleConfirm formData', formData);
      const orderRetractResponse = await retractOrderSAV(orderRef, {
        ...formData,
        assignedTo: formData?.assignedTo?._id,
        userId,
      });

      if (!orderRetractResponse) {
        setConfirmLoading(false);
        throw new Error('Failed to retract order');
      }
      const messageError = `La demande de rétractation de la commande ${orderRef} est enregistrée.`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'success' })
      );

      setIsConfirmOpen(false);
      setConfirmLoading(false);
      handleShowComponent(null);
    } catch (err) {
      console.error('Failed to send retraction request:', err);
      setConfirmLoading(false);
      const messageError = `Une erreur est survenue lors de la demande de rétractation de la commande ${orderRef}.`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'error' })
      );

      setIsConfirmOpen(false);
    }
  };

  return (
    <>
      <Card
        elevation={5}
        sx={{
          width: '100%',
          padding: 2,
          gap: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <RetractionFeesForm
          orderDetails={orderDetails}
          isChildComponent={true}
          getRetractionFees={getRetractionFees}
        />

        <FormControl fullWidth size="small" error={Boolean(formError.reason)}>
          <InputLabel>Motif de rétractation</InputLabel>
          <Select
            value={formData?.reason}
            label="Motif de rétractation"
            onChange={(e) =>
              handleFormChange({ key: 'reason', value: e.target.value })
            }
            sx={{ width: '50%' }}
          >
            {orderRetractionReasons.map((reason) => (
              <MenuItem key={reason?._id} value={reason?.name}>
                {reason?.name}
              </MenuItem>
            ))}
          </Select>
          {formError.reason && (
            <FormHelperText>{formError.reason}</FormHelperText>
          )}
        </FormControl>

        <FormControl
          error={Boolean(formError.comment)}
          fullWidth
          sx={{ mt: 2 }}
        >
          <Typography sx={{ fontSize: '0.8em' }}>Commentaire</Typography>
          <TextField
            multiline
            rows={4}
            value={formData.comment}
            onChange={(e) =>
              handleFormChange({ key: 'comment', value: e.target.value })
            }
            placeholder="Ajoutez des informations sur la rétractation. Cette note sera transmise au Service Logistique (SLO)"
            error={Boolean(formError.comment)}
            helperText={
              formError.comment || 'Texte à destination des opérateurs internes'
            }
          />
        </FormControl>

        <Box sx={{ position: 'relative' }}>
          <Typography
            variant="h6"
            color="primary"
            sx={{ fontSize: '1em', fontWeight: 'bold', mt: 1 }}
          >
            Assignation du nouveau ticket SLO (optionnel)
          </Typography>
          <UserAssignmentAutocomplete
            type={'ticket'}
            setSelectedUser={(user) =>
              handleFormChange({ key: 'assignedTo', value: user })
            }
            selectedUser={formData?.assignedTo}
            isSubmit={true}
            isRetraction={true}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            variant="outlined"
            color="info"
            onClick={() => handleShowComponent(null)}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenConfirm}
          >
            Confirmer
          </Button>
        </Box>
      </Card>
      <RetractionConfirmModal
        confirmLoading={confirmLoading}
        isOpen={isConfirmOpen}
        onConfirm={handleConfirm}
        onClose={onCloseModalConfirm}
        formData={formData}
        isSAV={true}
      />
    </>
  );
};

export default RetractionFormSAV;
